/**
 * Pricing state for a single Collection's checkout. Follows the
 * useState/useEffect `{data, loading, error, reload}` pattern used
 * elsewhere in this SPA (`useCollections.ts`, `usePublishEntitlement.ts`) —
 * no React Query in this app.
 *
 * The single most important property of this hook: it NEVER computes a
 * price itself. Every number comes from `calculatePrice()`'s response —
 * this hook only sequences *when* that call happens (initial load, coupon
 * apply, coupon remove) and reconciles the result into UI-friendly state.
 *
 * `quote` is deliberately "last successful calculation" (per the Pricing
 * Engine architecture's requested state shape) — a failed recalculation
 * (network error, or a rejected coupon) never clears it, so the UI can keep
 * showing the last-known-good, still-valid price alongside the new error
 * (Pricing Engine E2E flows, "existing valid price remains correct").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { calculatePrice, ProductUnavailableForPricingError } from './api';
import { describeCouponReason, PRICING_CALCULATION_FAILED_MESSAGE } from './copy';
import type { CouponLine, PriceQuote } from './types';

export interface UsePricingResult {
  /** The last successful Price Quote — the authoritative price to display. `null` only before the first calculation ever succeeds. */
  quote: PriceQuote | null;
  /** True only while the very first calculation is in flight (no quote yet). */
  loading: boolean;
  /** True while a later calculation (coupon apply/remove/retry) is in flight and a quote already exists. */
  recalculating: boolean;
  /** Set when a calculation attempt fails outright (network/5xx/502) — `quote`, if present, is preserved. */
  error: string | null;
  /** The Collection itself is unknown or inactive — a distinct case from a transient pricing failure. */
  productUnavailable: boolean;
  /** The coupon code currently reflected in `quote` as actually applied (never the code the user typed, if the backend rejected it). */
  appliedCouponCode: string | null;
  /** Set when a specific coupon was rejected by the backend — `quote` still reflects the correct, un-discounted price. */
  couponError: string | null;
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: () => Promise<void>;
  /** Re-runs the calculation with whatever coupon is currently applied (or none) — e.g. after a "Retry" click. */
  retry: () => Promise<void>;
}

export function usePricing(collectionCode: string | undefined): UsePricingResult {
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productUnavailable, setProductUnavailable] = useState(false);
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);

  // Guards against an out-of-order response overwriting a newer request's
  // result — same pattern as `useCollections.ts` (a fast coupon apply
  // followed immediately by remove must not let the apply's response land
  // after the remove's).
  const requestIdRef = useRef(0);
  const hasQuoteRef = useRef(false);

  const runCalculate = useCallback(
    async (couponCode: string | null) => {
      if (!collectionCode) {
        // No Collection to price yet (e.g. still resolving the route param)
        // — nothing to calculate, but `loading` must not get stuck `true`
        // forever waiting for a call that will never be made.
        setLoading(false);
        return;
      }
      const requestId = ++requestIdRef.current;
      const isFirstLoad = !hasQuoteRef.current;
      if (isFirstLoad) setLoading(true);
      else setRecalculating(true);
      setError(null);
      setProductUnavailable(false);

      try {
        const result = await calculatePrice({
          collectionCode,
          couponCode: couponCode ?? undefined,
        });
        if (requestId !== requestIdRef.current) return; // superseded by a newer request

        setQuote(result);
        hasQuoteRef.current = true;

        if (couponCode) {
          const couponLine = result.breakdown.find(
            (line): line is CouponLine => line.type === 'COUPON',
          );
          if (couponLine?.applied) {
            setAppliedCouponCode(couponCode);
            setCouponError(null);
          } else {
            // Backend rejected the coupon — do NOT show it as applied, and
            // never fail the whole calculation (architecture §11.2).
            setAppliedCouponCode(null);
            setCouponError(describeCouponReason(couponLine?.reason));
          }
        } else {
          setAppliedCouponCode(null);
          setCouponError(null);
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        if (err instanceof ProductUnavailableForPricingError) {
          setProductUnavailable(true);
        } else {
          // Never surface the raw backend/network error text to the customer
          // (architecture §12/§16: "Do not expose internal backend errors or
          // stack traces") — always the same generic, reviewed copy.
          setError(PRICING_CALCULATION_FAILED_MESSAGE);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setRecalculating(false);
        }
      }
    },
    [collectionCode],
  );

  useEffect(() => {
    hasQuoteRef.current = false;
    setQuote(null);
    setAppliedCouponCode(null);
    setCouponError(null);
    void runCalculate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionCode]);

  const applyCoupon = useCallback(
    (code: string) => {
      const trimmed = code.trim();
      // An empty/whitespace-only code is not "no coupon" (that's
      // `removeCoupon`'s job) and must not be posted to the backend as one
      // — guard here too, not only in `CouponInput`, since this hook is
      // itself exported and callable directly.
      if (!trimmed) return Promise.resolve();
      return runCalculate(trimmed);
    },
    [runCalculate],
  );
  const removeCoupon = useCallback(() => runCalculate(null), [runCalculate]);
  const retry = useCallback(
    () => runCalculate(appliedCouponCode),
    [runCalculate, appliedCouponCode],
  );

  return {
    quote,
    loading,
    recalculating,
    error,
    productUnavailable,
    appliedCouponCode,
    couponError,
    applyCoupon,
    removeCoupon,
    retry,
  };
}
